import React from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  capitalize,
} from "@mui/material";
import { CheckCircle, HourglassEmpty, HelpOutline, Check } from "@mui/icons-material";
import { Loan, useDealContext } from "@/contexts/DealContext";
import { useNavigate, useParams } from "react-router-dom";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { getLoanKeysToDisplay, getLoanStatus } from "@/utils/loanStatusUtils";
import { capitalizeFirstLetter } from "./LoanSetupStep";

// Icônes pour chaque statut
const statusIcons = {
  searching: <HourglassEmpty sx={{ color: "gray", fontSize: "1.1rem" }} />, // En cours de recherche
  found: <Check sx={{ color: "orange", fontSize: "1.1rem" }} />, // Trouvé mais non validé
  validated: <CheckCircle sx={{ color: "green", fontSize: "1.1rem" }} />, // Validé par l'utilisateur
  question: <HelpOutline sx={{ color: "lightblue", fontSize: "1.1rem" }} />, // Question posée à l'utilisateur
};

const LoanSidebar = () => {
  const { deal } = useDealContext();
  const navigate = useNavigate();
  const { loanId } = useParams();
  const selectedLoan = deal.loans.find((loan) => loan.id === loanId) ?? deal.loans[0];

  const LoanCard = (loan: Loan, loanIndex: number) => (
    <>
      <Card
        sx={{ marginLeft: loan.parentId ? 10 : 3, marginRight: 2, marginTop: 3 }}
        variant="outlined"
      >
        <CardActionArea
          data-active={selectedLoan.id === loan.id ? "true" : undefined}
          onClick={() => navigate(`/review/loan/${loan.id}`)}
          sx={{
            height: "100%",
            "&[data-active]": {
              backgroundColor: "action.selected",
              "&:hover": {
                backgroundColor: "action.selectedHover",
              },
              "& .MuiTouchRipple-root": {
                display: "none", // Disable ripple effect
              },
            },
          }}
        >
          <CardContent sx={{ height: "100%" }}>
            <Typography variant="caption" color="text.secondary">
              {capitalizeFirstLetter(loan.type?.replaceAll("_", " ") || "")}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium" }} component="div">
              {loan.name || `Loan ${loanIndex + 1}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Number(loan.amount?.amount?.value)
                ? Number(loan.amount?.amount?.value).toLocaleString("fi-FI", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    style: "currency",
                    currency: loan.amount?.currency?.value || "EUR",
                    currencyDisplay: "code",
                  })
                : ""}
            </Typography>
          </CardContent>
        </CardActionArea>
        {selectedLoan.id === loan.id && (
          <>
            <ListItem
              sx={{
                pl: 4,
                opacity: 1,
                pb: 0,
                cursor: "pointer",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
              onClick={() => navigate(`/review/loan/${loan.id}`)}
            >
              <Tooltip title={t`description and type`} arrow>
                <ListItemText
                  primary={t`Description`}
                  primaryTypographyProps={{
                    fontSize: "0.8rem",
                    mb: "2px",
                  }}
                />
              </Tooltip>
              <Tooltip title={loan.type ? t`Found` : t`Information needed`} arrow>
                <ListItemIcon>
                  {loan.type ? statusIcons.validated : statusIcons.question}
                </ListItemIcon>
              </Tooltip>
            </ListItem>

            {getLoanKeysToDisplay(loan).map((key) => {
              const { isDefined, status } = getLoanStatus(loan[key]);
              let statusIcon = statusIcons.searching;
              let statusTooltip = <Trans>Searching</Trans>;
              switch (status) {
                case "Found":
                  statusIcon = statusIcons.found;
                  statusTooltip = <Trans>Found</Trans>;
                  break;
                case "Validated":
                  statusIcon = statusIcons.validated;
                  statusTooltip = <Trans>Validated</Trans>;
                  break;
                case "Information needed":
                  statusIcon = statusIcons.question;
                  statusTooltip = <Trans>Information required</Trans>;
                  break;
                default:
                  break;
              }

              return (
                <ListItem
                  key={key}
                  dense
                  sx={{
                    pl: 4,
                    opacity: isDefined ? 1 : 0.5, // Grisé si non défini
                    cursor: isDefined ? "pointer" : "default", // Désactivé si non défini
                    "&:hover": isDefined ? { bgcolor: "#f5f5f5" } : "none",
                  }}
                  onClick={() => isDefined && navigate(`/review/loan/${loan.id}/${key}`)}
                >
                  <Tooltip title={key.replaceAll("_", " ")} arrow>
                    <ListItemText
                      primary={capitalize(key.replaceAll("_", " "))}
                      primaryTypographyProps={{
                        fontSize: "0.8rem",
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={statusTooltip}>
                    <ListItemIcon>{statusIcon}</ListItemIcon>
                  </Tooltip>
                </ListItem>
              );
            })}
          </>
        )}
      </Card>
    </>
  );
  return (
    <List
      sx={{
        width: 320,
        paddingTop: 0,
        bgcolor: "background.paper",
        height: "100%",
        overflowY: "auto",
        borderRight: "1px solid #ddd",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontSize: "0.8rem", color: "text.secondary", marginTop: "0.25rem" }}
      >
        Description
      </Typography>
      <h3 style={{ margin: 0 }}>{deal.name}</h3>
      {/* <Typography variant="caption" color="text.secondary">
              {deal?.loans[0]?.amount?.currency?.value &&
                (deal.loans
                  .filter((loan) => !loan.parentId)
                  .reduce((sum, loan) => {
                    const value = loan.amount?.amount?.value;
                    return sum + (!Number.isNaN(Number(value)) ? Number(value) : 0);
                  }, 0)
                  .toLocaleString("fi-FI", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    style: "currency",
                    currency: deal?.loans[0]?.amount?.currency?.value ?? "EUR",
                    currencyDisplay: "code",
                  }) ??
                  "")}
            </Typography> */}
      {deal.loans
        .filter((l) => l.parentId == null)
        .map((loan, loanIndex) => (
          <React.Fragment key={loan.id}>
            {/* Loan Title */}
            {LoanCard(loan, loanIndex)}
            {deal.loans
              .filter((l) => l.parentId == loan.id)
              .map((loan, loanIndex) => (
                <React.Fragment key={loan.id}>
                  {/* Loan Card */}
                  {LoanCard(loan, loanIndex)}
                </React.Fragment>
              ))}
          </React.Fragment>
        ))}
    </List>
  );
};

export default LoanSidebar;
